import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker to use local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

function PageThumbnail({ sessionId, fileId, pageNumber, isSelected, onToggle }) {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const renderThumbnail = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiUrl = process.env.REACT_APP_API_URL || '';
        const pdfUrl = `${apiUrl}/api/session/${sessionId}/file/${fileId}`;

        console.log('Loading PDF from:', pdfUrl);

        // Load the PDF document with explicit options
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          withCredentials: false,
        });
        const pdf = await loadingTask.promise;

        console.log('PDF loaded successfully, page count:', pdf.numPages);

        if (isCancelled) return;

        // Get the specific page
        const page = await pdf.getPage(pageNumber);

        if (isCancelled) return;

        // Calculate scale to fit thumbnail
        const viewport = page.getViewport({ scale: 1 });
        const scale = 150 / viewport.width; // Target width of 150px
        const scaledViewport = page.getViewport({ scale });

        // Prepare canvas
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        // Render PDF page to canvas
        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        };

        await page.render(renderContext).promise;

        console.log('Thumbnail rendered successfully for page', pageNumber);

        if (!isCancelled) {
          setLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Error rendering thumbnail:', err);
          console.error('Error details:', {
            name: err.name,
            message: err.message,
            stack: err.stack,
          });
          setError('Failed to load');
          setLoading(false);
        }
      }
    };

    renderThumbnail();

    return () => {
      isCancelled = true;
    };
  }, [sessionId, fileId, pageNumber]);

  return (
    <div
      onClick={onToggle}
      style={{
        ...styles.container,
        ...(isSelected ? styles.containerSelected : {}),
      }}
    >
      <div style={styles.thumbnailWrapper}>
        {loading && (
          <div style={styles.placeholder}>
            <span style={styles.loadingText}>Loading...</span>
          </div>
        )}
        {error && (
          <div style={styles.placeholder}>
            <span style={styles.errorText}>{error}</span>
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{
            ...styles.canvas,
            display: loading || error ? 'none' : 'block',
          }}
        />
      </div>
      <div style={styles.label}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          style={styles.checkbox}
          onClick={(e) => e.stopPropagation()}
        />
        <span>Page {pageNumber}</span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    border: '2px solid #dee2e6',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  containerSelected: {
    borderColor: '#007bff',
    backgroundColor: '#e7f3ff',
    boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
  },
  thumbnailWrapper: {
    position: 'relative',
    width: '150px',
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  canvas: {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
    padding: '10px',
  },
  loadingText: {
    fontSize: '12px',
    color: '#666',
  },
  errorText: {
    fontSize: '11px',
    color: '#dc3545',
    textAlign: 'center',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    fontWeight: '500',
  },
  checkbox: {
    cursor: 'pointer',
    width: '16px',
    height: '16px',
  },
};

export default PageThumbnail;
